import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, message, Avatar } from "@mimber/ui-web"
import { TeamOutlined, ClockCircleOutlined, UserOutlined } from "@mimber/ui-web"
import { useGroups } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { GroupDto } from "@mimber/schema"
import type { GroupsTableProps } from "./GroupsTable.types"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
  getGroupTypeDisplay,
  getGroupVisibilityDisplay,
} from "@/utils"

const { Text } = Typography

export const GroupsTable: React.FunctionComponent<GroupsTableProps> = ({
  onRowClick,
  className,
  ...tableProps
}) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "groups",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
    })

  const { data: response, isLoading, error } = useGroups(queryParams)

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load groups: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<GroupDto> = useMemo(
    () => [
      {
        title: "Group",
        key: "group",
        render: (_, record) => (
          <Space>
            <Avatar icon={<TeamOutlined />} size="default" />
            <div>
              <div>
                <Text strong>{record.name}</Text>
              </div>
              {record.description && (
                <div>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    {record.description.length > 50
                      ? `${record.description.substring(0, 50)}...`
                      : record.description}
                  </Text>
                </div>
              )}
            </div>
          </Space>
        ),
        sorter: true,
        sortDirections: ["ascend", "descend"],
        ...getColumnSearchProps("Search group name"),
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        render: (type: string) => {
          const { label, color } = getGroupTypeDisplay(type)
          return <Tag color={color}>{label}</Tag>
        },
        filters: [
          { text: "Committee", value: "COMMITTEE" },
          { text: "Group", value: "GROUP" },
        ],
      },
      {
        title: "Visibility",
        dataIndex: "visibility",
        key: "visibility",
        render: (visibility: string) => {
          const { label, color } = getGroupVisibilityDisplay(visibility)
          return <Tag color={color}>{label}</Tag>
        },
        filters: [
          { text: "Public", value: "PUBLIC" },
          { text: "Private", value: "PRIVATE" },
        ],
      },
      {
        title: "Members",
        key: "memberCount",
        render: (_, record) => (
          <Space>
            <UserOutlined />
            <Text>{record._count?.members || 0}</Text>
          </Space>
        ),
        sorter: true,
        sortDirections: ["ascend", "descend"],
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (createdAt: string) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(createdAt)}</Text>
          </Space>
        ),
      },
    ],
    []
  )

  const handleRowClick = (record: GroupDto) => {
    if (onRowClick) {
      onRowClick(record)
    }
  }

  const onTableChange: TableProps<GroupDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    // Convert antd types to our utility function types
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<GroupDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
